import React, { useState } from 'react'
import { matchPath, useHistory } from 'react-router'
import { Formik, FormikProps } from 'formik'
import { Button, ModalInjectedProps } from '@sketch/components'
import {
  GetProfilePublicationsDocument,
  GetWorkspaceProfileIdentifierDocument,
  useCreateWorkspaceProfileMutation,
  useGetSharesForDocumentSelectionQuery,
  useUpdateWorkspaceProfileMutation,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { routes } from '@sketch/modules-common'
import {
  Modal,
  ModalBody,
  OrderedBreadcrumbs,
  InfoNoteContainer,
  InformationIcon,
} from './ProfileModal.styles'

import { IntroductionStep } from './IntroductionStep'
import { ProfileDetailsStep } from './ProfileDetailsStep'
import { SelectDocumentsStep } from './SelectDocumentsStep'
import { useProfileShortURL } from '../../hooks/useProfileShortURL'
import { initialValues, validationSchema } from './form'
import { useGetWorkspaceProfile } from '../../hooks'
import { useToast } from '@sketch/toasts'
import { useLocation } from 'react-use'

const getBreadcrumbs = (isProfileEditRoute: boolean) => {
  const breadcrumbs = []

  if (!isProfileEditRoute) {
    breadcrumbs.push({ content: 'Public Profile' })
  }

  breadcrumbs.push({ content: 'Details' }, { content: 'Documents' })

  return breadcrumbs
}

interface ProfileModalProps extends ModalInjectedProps {
  workspace: WorkspaceMinimalFragment
}

export const ProfileModal = ({ workspace, hideModal }: ProfileModalProps) => {
  const history = useHistory()

  const { pathname } = useLocation()
  const isProfileEditRoute = !!matchPath(
    pathname ?? '',
    routes.WORKSPACE_PROFILE_EDIT.template()
  )

  const { showToast } = useToast()

  const profileResult = useGetWorkspaceProfile({
    variables: {
      workspaceIdentifier: workspace.identifier,
    },
  })
  const profileData = profileResult.profile
  const profileIdentifier = profileData?.identifier
  const shortUrlName = profileData?.shortUrlName || ''
  const description = profileData?.description || ''

  const [step, setStep] = useState(isProfileEditRoute ? 1 : 0)

  const { currentShortUrl } = useProfileShortURL(shortUrlName)
  const composedInitialFormValue = {
    ...initialValues,
    shortUrlName: currentShortUrl,
    description,
  }

  const [createWorkspaceProfile, { loading: createWorkspaceProfileLoading }] =
    useCreateWorkspaceProfileMutation({
      onError: 'show-toast',
      onCompleted: () => {
        history.push(
          routes.WORKSPACE_PROFILE_EDIT.create({
            workspaceId: workspace.identifier,
          })
        )
      },
    })

  const [updateWorkspaceProfile, { loading: updateWorkspaceProfileLoading }] =
    useUpdateWorkspaceProfileMutation({
      onError: 'show-toast',
      onCompleted: () => {
        showToast('Profile successfully updated')
        hideModal()
      },
    })

  const { data, loading: loadingShares } =
    useGetSharesForDocumentSelectionQuery({
      variables: {
        identifier: workspace.identifier,
        search: {
          filter: 'EXCLUDE_PERSONAL_DRAFTS',
        },
      },
      fetchPolicy: 'cache-and-network',
    })

  const loading =
    createWorkspaceProfileLoading ||
    loadingShares ||
    updateWorkspaceProfileLoading

  const prevStep = () => {
    setStep(prev => prev - 1)
  }

  const nextStep = () => {
    setStep(prev => prev + 1)
  }

  const renderStep = (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0:
        return <IntroductionStep />
      case 1:
        return (
          <ProfileDetailsStep workspace={workspace} formikBag={formikBag} />
        )
      case 2:
        return (
          <SelectDocumentsStep workspace={workspace} formikBag={formikBag} />
        )
    }
  }

  const handleSecondaryButtonAction = () => {
    const shouldHideModal = isProfileEditRoute ? step === 1 : step === 0
    shouldHideModal ? hideModal() : prevStep()
  }

  const getSecondaryButtonText = () => {
    const shouldHideModal = isProfileEditRoute ? step === 1 : step === 0
    return shouldHideModal ? 'Cancel' : 'Back'
  }

  const handleSubmitButtonAction = async (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0: {
        nextStep()
        break
      }
      case 1: {
        const { shares, ...stepOneValues } = initialValues
        // Given that shortUrlName validation is not part of formik, we need
        // to make sure the there's no current error with shortUrlName before
        // triggering validation
        if (formikBag.errors.shortUrlName) {
          return
        }

        const errors = await formikBag.validateForm()
        formikBag.setTouched(
          Object.keys(stepOneValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {} as Record<string, boolean>
          )
        )

        if (Object.keys(errors).length === 0) {
          nextStep()
        }
        break
      }
      case 2: {
        const { values } = formikBag
        const { selectAll, checkProfileURLLoading, ...formValues } = values

        if (isProfileEditRoute) {
          updateWorkspaceProfile({
            variables: {
              input: { identifier: profileIdentifier, ...formValues },
            },
            refetchQueries: [
              {
                query: GetProfilePublicationsDocument,
                variables: { workspaceIdentifier: workspace.identifier },
              },
            ],
          })
        } else {
          createWorkspaceProfile({
            variables: {
              workspaceIdentifier: workspace.identifier,
              input: { ...formValues },
            },
            refetchQueries: [
              {
                query: GetWorkspaceProfileIdentifierDocument,
                variables: { identifier: workspace.identifier },
              },
            ],
            awaitRefetchQueries: true,
          })
        }
        break
      }
    }
  }

  const getSubmitButtonText = () => {
    switch (step) {
      case 0:
        return 'Get Started'
      case 1:
        return 'Choose Documents'
      case 2:
        return isProfileEditRoute ? 'Save' : 'Create Public Profile'
    }
  }

  const renderFooterNote = () => {
    if (step === 2) {
      return (
        <InfoNoteContainer>
          <InformationIcon />
          You can preview your profile before publishing it.
        </InfoNoteContainer>
      )
    }
  }

  const sharesWithPublication = data?.workspace.shares?.entries
    ?.filter(({ publication }) => !!publication)
    .map(({ identifier }) => identifier)

  return (
    <Formik
      initialValues={{
        ...composedInitialFormValue,
        shares: sharesWithPublication,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      // eslint-disable-next-line no-console
      onSubmit={() => {}}
    >
      {formikBag => (
        <Modal onCancel={hideModal}>
          <ModalBody>
            <OrderedBreadcrumbs
              currentCrumb={step}
              crumbs={getBreadcrumbs(isProfileEditRoute)}
              showAllInMobile
            />

            {renderStep(formikBag)}
          </ModalBody>
          <Modal.Footer leftContent={renderFooterNote()}>
            <Button
              variant="secondary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              size="40"
              onClick={() => handleSecondaryButtonAction()}
            >
              {getSecondaryButtonText()}
            </Button>
            <Button
              variant="primary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              loading={loading}
              size="40"
              onClick={() => handleSubmitButtonAction(formikBag)}
            >
              {getSubmitButtonText()}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}
